import type { Client } from '../../api/client.js';
import type { Bien, Estimation, QuestionBeta, Scenario } from './types.js';

export const CLE_VISIBILITE_CLIENT = 'estimation.visible_client';
const CONTEXTE_BETA = 'D04c.afficher_au_client';
/** BETA-27 (lot 4, point 8 c) : faut-il ajuster la valeur du terrain selon que le titre est trouvé ? */
export const CLE_VALEUR_TERRAIN = 'titre.ajuster_valeur_terrain';
const CONTEXTE_VALEUR_TERRAIN = 'D04c.valeur_terrain';

export interface DonneesEstimation {
  estimation: Estimation;
  biens: readonly Bien[];
  /** Vrai dès que l'arbre du dossier porte au moins une personne. */
  arbreComplet: boolean;
  vivants: number;
  question: QuestionBeta | null;
  /** Question BETA-27, posée à part de la visibilité client ; null si déjà répondue ou illisible. */
  questionValeurTerrain: QuestionBeta | null;
}

interface ArbreLu {
  nodes: ReadonlyArray<{ vitalStatus: string }>;
}

type Appeleur = Pick<Client, 'appeler'>;

/** Ce qui est accessoire (biens, arbre, question BETA) ne doit jamais empêcher d'afficher l'estimation. */
const ouRepli = async <T>(promesse: Promise<T>, repli: T): Promise<T> => promesse.catch(() => repli);

export async function chargerEstimation(client: Appeleur, dossierId: string, scenario: Scenario): Promise<DonneesEstimation> {
  const parametres = { dossierId };
  const [estimation, biens, arbre, questions, questionsTerrain] = await Promise.all([
    client.appeler<Estimation>('estimations.lire', { parametres, requete: { scenario } }),
    ouRepli(client.appeler<Bien[]>('biens.lister', { parametres }), [] as Bien[]),
    ouRepli(client.appeler<ArbreLu>('genealogie.arbre', { parametres }), { nodes: [] } as ArbreLu),
    ouRepli(client.appeler<QuestionBeta[]>('beta.questionsDues', { requete: { contexte: CONTEXTE_BETA } }), [] as QuestionBeta[]),
    ouRepli(client.appeler<QuestionBeta[]>('beta.questionsDues', { requete: { contexte: CONTEXTE_VALEUR_TERRAIN } }), [] as QuestionBeta[]),
  ]);
  return {
    estimation,
    biens,
    arbreComplet: arbre.nodes.length > 0,
    vivants: arbre.nodes.filter((n) => n.vitalStatus === 'vivant').length,
    question: questions.find((q) => q.key === CLE_VISIBILITE_CLIENT) ?? null,
    questionValeurTerrain: (Array.isArray(questionsTerrain) ? questionsTerrain : []).find((q) => q.key === CLE_VALEUR_TERRAIN) ?? null,
  };
}

export async function repondreVisibiliteClient(client: Appeleur, option: string): Promise<void> {
  await client.appeler('beta.repondre', {
    parametres: { cle: CLE_VISIBILITE_CLIENT },
    corps: { option, context: { ecran: 'D04c' } },
  });
}
